import {
  AvailableSchedulePeriod,
  ITimeSlotTimestamp,
} from '../models/schedule-calendar.models';
import {
  IDaySegment,
  MINUTES_PER_DAY,
  splitByDay,
} from './schedule-timezone.utils';

const MILLISECONDS_PER_MINUTE = 60_000;

/** Vertical scale of the calendar grid */
export const CALENDAR_PIXELS_PER_MINUTE = 0.9;

export const minutesToPixels = (minutes: number) =>
  minutes * CALENDAR_PIXELS_PER_MINUTE;

/** The whole day is on the calendar, which scrolls to show part of it */
export const CALENDAR_HEIGHT = minutesToPixels(MINUTES_PER_DAY);

/** Minutes at the top of each hour, from the start to the end of the day */
export const CALENDAR_HOURS = Array.from({ length: 25 }).map((_, i) => i * 60);

/** The part of the day the calendar is sized to and initially scrolled to */
export const DEFAULT_VISIBLE_MINUTE_RANGE: ICalendarMinuteRange = {
  start: 8 * 60,
  end: 20 * 60,
};

/** Minutes from the start of the day */
export interface ICalendarMinuteRange {
  start: number;
  end: number;
}

export type CalendarDayItem = ICalendarMinuteRange & {
  /** YYYY-MM-DD format in local / selected timezone */
  date: string;
};

/** Contiguous available time on one day, with the preferred ranges inside it */
export type CalendarAvailableBlock = CalendarDayItem & {
  preferred: ICalendarMinuteRange[];
  /** The block as UTC timestamps */
  range: ITimeSlotTimestamp;
};

/** A time selected on one day, as minutes on its day and as UTC timestamps */
export type CalendarTimeSlot = CalendarDayItem & {
  range: ITimeSlotTimestamp;
};

/** Something scheduled, such as a call, on one day */
export type CalendarItem<T extends ITimeSlotTimestamp> = CalendarDayItem & {
  item: T;
};

/** Day items that overlap, spanning from the first start to the last end */
export type CalendarItemGroup<T extends CalendarDayItem> = CalendarDayItem & {
  /** In order of their start */
  items: T[];
};

export interface ICalendarDay {
  /** YYYY-MM-DD format in local / selected timezone */
  date: string;
  isToday: boolean;
  isInPast: boolean;
}

/** Minutes to HH:mm */
export const formatMinutes = (minutes: number) =>
  [Math.floor(minutes / 60), minutes % 60]
    .map((value) => String(value).padStart(2, '0'))
    .join(':');

export const formatMinuteRange = ({ start, end }: ICalendarMinuteRange) =>
  [formatMinutes(start), formatMinutes(end)].join('-');

/**
 * Splits the part of a period within the visible range into days in the
 * timezone, so a period spanning years only costs the days on show. A period
 * running into or out of the range keeps the part inside it.
 */
const splitVisibleByDay = (
  period: ITimeSlotTimestamp,
  visibleRange: ITimeSlotTimestamp,
  timezoneName: string
) => {
  const start = Math.max(
    Date.parse(period.start),
    Date.parse(visibleRange.start)
  );
  const end = Math.min(Date.parse(period.end), Date.parse(visibleRange.end));

  return start < end
    ? splitByDay(
        {
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
        },
        timezoneName
      )
    : [];
};

/**
 * Minutes from a fixed UTC time to the start of the segment's day on its wall
 * clock, which is the same for segments on a day only where they share an
 * offset
 */
const toWallClockOffset = ({
  start,
  range,
}: Pick<IDaySegment, 'start' | 'range'>) =>
  Date.parse(range.start) / MILLISECONDS_PER_MINUTE - start;

/**
 * Splits the periods within the visible range into days in the timezone,
 * then merges touching or overlapping periods on the same day into one block,
 * keeping the preferred periods as ranges within it. Periods either side of
 * the clocks changing stay separate, as their wall clock times can overlap
 * without the periods doing so.
 */
export const groupAvailableBlocks = (
  periods: AvailableSchedulePeriod[],
  visibleRange: ITimeSlotTimestamp,
  timezoneName: string
): CalendarAvailableBlock[] =>
  periods
    .flatMap((period) =>
      splitVisibleByDay(period, visibleRange, timezoneName).map((segment) => ({
        ...segment,
        isPreferred: period.isPreferred,
      }))
    )
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.range.start.localeCompare(b.range.start)
    )
    .reduce<CalendarAvailableBlock[]>(
      (blocks, { date, start, end, range, isPreferred }) => {
        const preferred = isPreferred ? [{ start, end }] : [];
        const last = blocks.at(-1);

        if (
          last?.date !== date ||
          toWallClockOffset(last) !== toWallClockOffset({ start, range }) ||
          start > last.end
        ) {
          return [...blocks, { date, start, end, preferred, range }];
        }

        return [
          ...blocks.slice(0, -1),
          {
            ...last,
            ...(end > last.end && {
              end,
              range: {
                ...last.range,
                end: range.end,
              },
            }),
            preferred: [...last.preferred, ...preferred],
          },
        ];
      },
      []
    );

/**
 * Items within the visible range in the selected timezone. An item crossing
 * midnight is split into one per day, each keeping the whole item.
 */
export const mapItemsToTimezone = <T extends ITimeSlotTimestamp>(
  items: T[],
  visibleRange: ITimeSlotTimestamp,
  timezoneName: string
): CalendarItem<T>[] =>
  items.flatMap((item) =>
    splitVisibleByDay(item, visibleRange, timezoneName).map(
      ({ date, start, end }) => ({
        date,
        start,
        end,
        item,
      })
    )
  );

/** Groups day items by their date */
export const groupByDate = <T extends CalendarDayItem>(items: T[]) =>
  items.reduce(
    (byDate, item) =>
      byDate.set(item.date, [...(byDate.get(item.date) ?? []), item]),
    new Map<string, T[]>()
  );

/**
 * Groups day items that overlap on the same day, including through a chain of
 * overlaps. Items that only touch, one ending as the next starts, stay apart.
 */
export const groupOverlapping = <T extends CalendarDayItem>(
  items: T[]
): CalendarItemGroup<T>[] =>
  [...items]
    .sort((a, b) => a.date.localeCompare(b.date) || a.start - b.start)
    .reduce<CalendarItemGroup<T>[]>((groups, item) => {
      const last = groups.at(-1);

      if (last?.date !== item.date || item.start >= last.end) {
        return [
          ...groups,
          {
            date: item.date,
            start: item.start,
            end: item.end,
            items: [item],
          },
        ];
      }

      return [
        ...groups.slice(0, -1),
        {
          ...last,
          end: Math.max(last.end, item.end),
          items: [...last.items, item],
        },
      ];
    }, []);

/** Whether a block has fully ended, by its day or by the current time today */
export const isBlockInPast = (
  day: ICalendarDay,
  block: ICalendarMinuteRange,
  nowMinutes: number
) => day.isInPast || (day.isToday && block.end <= nowMinutes);

/**
 * The earliest start before, and the latest end after, the visible range of
 * the items. Null when nothing is hidden that way.
 */
export const getHiddenMinutes = (
  items: ICalendarMinuteRange[],
  visible: ICalendarMinuteRange
) => {
  const starts = items
    .map(({ start }) => start)
    .filter((start) => start < visible.start);
  const ends = items.map(({ end }) => end).filter((end) => end > visible.end);

  return {
    earlier: starts.length ? Math.min(...starts) : null,
    later: ends.length ? Math.max(...ends) : null,
  };
};

/** Selected times start on these increments from the start of their block */
export const SELECT_TIME_INCREMENT = 15;

/**
 * The start of a time of the duration at a minute in the block, rounded down
 * to an increment from the block's start and moved to fit fully within the
 * block, starting no earlier than `earliest`. Null when no time fits.
 */
export const getSlotStart = (
  block: ICalendarMinuteRange,
  duration: number,
  at: number,
  earliest = 0
) => {
  const toIncrement = (minutes: number, round: (value: number) => number) =>
    block.start +
    round((minutes - block.start) / SELECT_TIME_INCREMENT) *
      SELECT_TIME_INCREMENT;

  const first = toIncrement(Math.max(block.start, earliest), Math.ceil);
  const last = toIncrement(block.end - duration, Math.floor);

  return first <= last
    ? Math.min(Math.max(toIncrement(at, Math.floor), first), last)
    : null;
};

/**
 * A time within the block. Its wall clock offset is the block's, as blocks
 * either side of the clocks changing are kept apart.
 */
export const toBlockSlot = (
  block: CalendarAvailableBlock,
  start: number,
  duration: number
): CalendarTimeSlot => {
  const rangeStart =
    Date.parse(block.range.start) +
    (start - block.start) * MILLISECONDS_PER_MINUTE;

  return {
    date: block.date,
    start,
    end: start + duration,
    range: {
      start: new Date(rangeStart).toISOString(),
      end: new Date(
        rangeStart + duration * MILLISECONDS_PER_MINUTE
      ).toISOString(),
    },
  };
};
